import { BadRequest, ServiceUnavailable } from 'common/errors'
import { useEffect, useState } from 'react'
import { resetPassword } from '../api/website-public-page-api'
import { PublicPageFieldSlugEnum } from '../enums/public-page-field-slug-enum'
import { validateEmptyField } from '../utils/validation'

type ErrorFields = Partial<Record<PublicPageFieldSlugEnum | 'root', string[]>>

export const useResetPasswordSubmit = (token: string) => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ErrorFields>()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.Password]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.Password]: undefined,
      }))
    }
  }, [password])

  useEffect(() => {
    if (errors?.[PublicPageFieldSlugEnum.ConfirmPassword]) {
      setErrors(prev => ({
        ...prev,
        [PublicPageFieldSlugEnum.ConfirmPassword]: undefined,
      }))
    }
  }, [confirmPassword])

  const handleSubmit = async () => {
    const passwordError = validateEmptyField(password)
    if (passwordError && passwordError.length > 0) {
      setErrors({ [PublicPageFieldSlugEnum.Password]: passwordError })
      return
    }

    const confirmPasswordError = validateEmptyField(confirmPassword)
    if (confirmPasswordError && confirmPasswordError.length > 0) {
      setErrors({
        [PublicPageFieldSlugEnum.ConfirmPassword]: confirmPasswordError,
      })
      return
    }

    if (password !== confirmPassword) {
      setErrors({
        [PublicPageFieldSlugEnum.ConfirmPassword]: [
          'validation.password.mismatch',
        ],
      })
      return
    }

    setErrors(undefined)

    try {
      setLoading(true)
      const { data } = await resetPassword({ password, token })
      if (data?.redirect) {
        window.location.href = data.redirect
      }
    } catch (e) {
      if (e instanceof ServiceUnavailable) {
        setErrors({ root: ['core.errors.service_temporary_unavailable'] })
      } else if (e instanceof BadRequest) {
        const { fields, common } = e.response?.data?.errors || {}
        const newErrors: ErrorFields = {}
        if (fields) {
          Object.entries(fields).forEach(([key, messages]) => {
            if (key === PublicPageFieldSlugEnum.Password) {
              newErrors[key as PublicPageFieldSlugEnum] = [messages?.join('\n')]
            }
          })
        }
        if (common) {
          newErrors.root = [common?.join('\n')]
        }
        setErrors(newErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    errors,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    handleSubmit,
  }
}
